// Registre de couverture documentaire : fabricant › gamme › référence, par usage, avec la règle de statut affichée.
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Layers, Upload } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, pluriel } from '../../lib/format.ts';
import { Alerte, Chargement, EntetePage, EtatErreur, EtatVide, LienBouton, Panneau } from '../../components/ui.tsx';
import { BadgeCouverture, NavDocumentation, STATUTS_COUVERTURE, TYPES_DOCUMENT, libelleType } from './commun.tsx';

interface RefCouverture {
  reference: string | null; manufacturer: string | null; range_name: string | null; nb_equipements: number; dans_parc: boolean;
  statut: string; usages: Record<string, string>; manquants: string[];
  documents: { id: string; title: string; doc_type: string; version_id: string; version_label: string; statut: string }[];
  derniere_verification: string | null; derniere_publication: string | null;
}
interface Reponse {
  regle: { essentiels: string[]; lignes: string[] };
  totaux: Record<string, number>;
  equipements_sans_reference: number;
  references: RefCouverture[];
}

const ORDRE = ['acces_a_obtenir', 'incomplet', 'non_valide', 'obsolete', 'disponible'];
const LIBELLE_USAGE: Record<string, string> = { disponible: 'validé', non_valide: 'non validé', obsolete: 'obsolète', absent: 'absent' };

export function PageCouverture() {
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const filtre = params.get('statut') ?? '';
  const parcSeul = params.get('parc') === '1';
  const q = useQuery({ queryKey: ['documents-couverture'], enabled: peut('doc.lire'), queryFn: () => api.get<Reponse>('/api/documents/couverture') });

  const groupes = useMemo(() => {
    const lignes = (q.data?.references ?? []).filter((r) => (!filtre || r.statut === filtre) && (!parcSeul || r.dans_parc));
    const parFab = new Map<string, Map<string, RefCouverture[]>>();
    for (const r of lignes) {
      const f = r.manufacturer ?? 'Fabricant inconnu';
      const g = r.range_name ?? 'Gamme non précisée';
      if (!parFab.has(f)) parFab.set(f, new Map());
      const m = parFab.get(f)!;
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(r);
    }
    return { lignes, parFab };
  }, [q.data, filtre, parcSeul]);

  const changer = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  if (!peut('doc.lire')) return <Panneau><EtatVide titre="Accès non autorisé" texte="Vos droits ne permettent pas de consulter la documentation." /></Panneau>;

  return (
    <div className="pile-l doc-module">
      <EntetePage titre="Couverture documentaire"
        sousTitre="Pour chaque référence du parc et chaque référence documentée : ce qui est disponible, à valider, obsolète ou encore à obtenir." />
      <NavDocumentation peutImporter={peut('doc.importer')} />

      {q.isLoading && <Chargement lignes={5} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && (
        <>
          <details className="panneau doc-regle">
            <summary>Règle de calcul du statut</summary>
            <ul>{q.data.regle.lignes.map((l) => <li key={l}>{l}</li>)}</ul>
            <p className="petit secondaire">Seuls les documents de la bibliothèque comptent, rattachés par référence exacte. La date de dernière vérification
              (contrôle humain de fraîcheur) est distincte de la date de publication du document.</p>
          </details>

          {q.data.equipements_sans_reference > 0 && (
            <Alerte ton="ambre" titre={`${pluriel(q.data.equipements_sans_reference, 'équipement')} sans référence exacte`}>
              Leur couverture documentaire ne peut pas être évaluée. Relevez la référence sur la plaque signalétique et complétez leur fiche.
            </Alerte>
          )}

          <div className="doc-filtres-statut" role="group" aria-label="Filtrer par statut">
            <button type="button" className="doc-bascule" aria-pressed={!filtre} onClick={() => changer('statut', null)}>
              Tous <span className="chiffre">{q.data.references.length}</span>
            </button>
            {ORDRE.map((s) => (
              <button key={s} type="button" className="doc-bascule" aria-pressed={filtre === s} onClick={() => changer('statut', filtre === s ? null : s)}>
                {STATUTS_COUVERTURE[s].libelle} <span className="chiffre">{q.data!.totaux[s] ?? 0}</span>
              </button>
            ))}
            <label className="case petit doc-case-parc">
              <input type="checkbox" checked={parcSeul} onChange={(e) => changer('parc', e.target.checked ? '1' : null)} />
              <span>Références du parc seulement</span>
            </label>
          </div>

          {q.data.references.length === 0 && (
            <Panneau><EtatVide icone={<Layers />} titre="Rien à évaluer pour l’instant"
              texte="Aucun équipement avec référence dans les sites accessibles, et aucun document de bibliothèque rattaché à une référence."
              action={peut('doc.importer') ? <LienBouton to="/documentation/importer" variante="principal">Importer des documents</LienBouton> : undefined} /></Panneau>
          )}
          {q.data.references.length > 0 && groupes.lignes.length === 0 && (
            <Panneau><EtatVide titre="Aucune référence pour ce filtre" texte="Choisissez un autre statut." /></Panneau>
          )}

          {[...groupes.parFab.entries()].map(([fab, gammes]) => (
            <Panneau key={fab} serre titre={fab}>
              {[...gammes.entries()].map(([gamme, refs]) => (
                <section key={gamme} className="doc-gamme" aria-label={`Gamme ${gamme}`}>
                  <h3 className="doc-gamme-titre">{gamme}</h3>
                  <ul className="doc-couverture">
                    {refs.map((r, i) => (
                      <li key={`${r.reference ?? 'gamme'}-${i}`} className="doc-couverture-ligne">
                        <div className="doc-couverture-tete">
                          <span className="doc-couverture-ref">
                            {r.reference ? <span className="mono">{r.reference}</span> : <span className="secondaire">Documents de gamme (sans référence)</span>}
                          </span>
                          <BadgeCouverture statut={r.statut} />
                          <span className="petit secondaire">{r.dans_parc ? pluriel(r.nb_equipements, 'équipement') + ' dans le parc' : 'Hors parc'}</span>
                        </div>
                        <dl className="doc-usages">
                          {q.data!.regle.essentiels.map((u) => {
                            const s = r.usages[u] ?? 'absent';
                            return <div key={u} className={`doc-usage doc-usage-${s}`}><dt>{TYPES_DOCUMENT[u]}</dt><dd>{LIBELLE_USAGE[s]}</dd></div>;
                          })}
                        </dl>
                        {r.documents.length > 0 && (
                          <ul className="doc-couverture-docs">
                            {r.documents.map((d) => (
                              <li key={d.id}>
                                <Link to={`/documentation/${d.id}`}>{d.title}</Link>
                                <span className="tres-petit discret"> — {libelleType(d.doc_type)}, version {d.version_label}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="doc-couverture-pied">
                          <span className="tres-petit secondaire">Dernière vérification : {r.derniere_verification ? date(r.derniere_verification) : 'jamais'}</span>
                          <span className="tres-petit secondaire">Publication : {r.derniere_publication ? date(r.derniere_publication) : 'inconnue'}</span>
                          {peut('doc.importer') && r.statut !== 'disponible' && (
                            <LienBouton compact variante="fantome" icone={<Upload aria-hidden />}
                              to={`/documentation/importer?${new URLSearchParams({
                                ...(r.manufacturer ? { fabricant: r.manufacturer } : {}), ...(r.range_name ? { gamme: r.range_name } : {}),
                                ...(r.reference ? { ref: r.reference } : {}), ...(r.manquants[0] ? { type: r.manquants[0] } : {}) })}`}>
                              Importer{r.manquants[0] ? ` : ${TYPES_DOCUMENT[r.manquants[0]].toLowerCase()}` : ''}
                            </LienBouton>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </Panneau>
          ))}
        </>
      )}
    </div>
  );
}
